import React, { useState } from "react";

/**
 * SecuritySettings
 *
 * Manage password policies, two-factor authentication, user-level restrictions, etc.
 * - Toggle 2FA
 * - Password complexity rules
 * - IP Whitelisting
 */
const SecuritySettings = () => {
  // 2FA
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);

  // Password Complexity
  const [minPasswordLength, setMinPasswordLength] = useState(8);
  const [requireNumbers, setRequireNumbers] = useState(true);
  const [requireSymbols, setRequireSymbols] = useState(false);

  // IP Whitelisting
  const [ipList, setIpList] = useState(["192.168.0.1"]);
  const [newIp, setNewIp] = useState("");

  // Save Handler
  const handleSave = () => {
    console.log("Security settings saved:", {
      twoFactorEnabled,
      minPasswordLength,
      requireNumbers,
      requireSymbols,
      ipList,
    });
    alert("Security settings have been saved (simulated)!");
  };

  const handleAddIp = () => {
    if (!newIp.trim()) {
      alert("Please enter a valid IP address.");
      return;
    }
    setIpList([...ipList, newIp.trim()]);
    setNewIp("");
  };

  const handleRemoveIp = (idx) => {
    const updated = [...ipList];
    updated.splice(idx, 1);
    setIpList(updated);
  };

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-8">
      {/* Page Title */}
      <h1 className="text-2xl font-bold mb-4 bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
        Security & Access Control
      </h1>
      <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
        Manage password policies, two-factor authentication, user-level restrictions, and more.
      </p>

      {/* Two-Factor Authentication */}
      <section className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 
                         rounded-lg p-6 shadow-sm space-y-4 transition-colors">
        <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-100">
          Two-Factor Authentication
        </h2>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Require users to provide an additional verification code when logging in.
        </p>
        <div className="flex items-center">
          <input
            id="twoFactorEnabled"
            type="checkbox"
            className="mr-2 w-4 h-4 accent-blue-600"
            checked={twoFactorEnabled}
            onChange={(e) => setTwoFactorEnabled(e.target.checked)}
          />
          <label htmlFor="twoFactorEnabled" className="text-gray-700 dark:text-gray-300">
            Enable Two-Factor Authentication
          </label>
        </div>
      </section>

      {/* Password Complexity */}
      <section className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 
                         rounded-lg p-6 shadow-sm space-y-4 transition-colors">
        <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-100">
          Password Complexity
        </h2>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Adjust minimum length and optional special character requirements.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Min Password Length */}
          <div className="flex flex-col">
            <label
              htmlFor="minPasswordLength"
              className="font-medium text-gray-700 dark:text-gray-300"
            >
              Minimum Password Length
            </label>
            <input
              id="minPasswordLength"
              type="number"
              className="mt-1 p-2 border border-gray-300 dark:border-gray-600 rounded 
                         focus:outline-none focus:ring-2 focus:ring-blue-500 
                         dark:bg-gray-700 dark:text-gray-100 w-24"
              min={1}
              value={minPasswordLength}
              onChange={(e) => setMinPasswordLength(Number(e.target.value))}
            />
          </div>
          <div className="flex flex-col justify-center space-y-2">
            {/* Require Numbers */}
            <div className="flex items-center">
              <input
                id="requireNumbers"
                type="checkbox"
                className="mr-2 w-4 h-4 accent-blue-600"
                checked={requireNumbers}
                onChange={(e) => setRequireNumbers(e.target.checked)}
              />
              <label htmlFor="requireNumbers" className="text-gray-700 dark:text-gray-300">
                Require at least one number
              </label>
            </div>
            {/* Require Symbols */}
            <div className="flex items-center">
              <input
                id="requireSymbols"
                type="checkbox"
                className="mr-2 w-4 h-4 accent-blue-600"
                checked={requireSymbols}
                onChange={(e) => setRequireSymbols(e.target.checked)}
              />
              <label htmlFor="requireSymbols" className="text-gray-700 dark:text-gray-300">
                Require at least one symbol (!@#$, etc.)
              </label>
            </div>
          </div>
        </div>
      </section>

      {/* IP Whitelisting */}
      <section className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 
                         rounded-lg p-6 shadow-sm space-y-4 transition-colors">
        <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-100">
          IP Whitelisting
        </h2>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Restrict access to specific IP addresses for enhanced security.
        </p>
        <div className="space-y-2">
          {ipList.map((ip, idx) => (
            <div
              key={idx}
              className="flex items-center justify-between bg-gray-50 dark:bg-gray-700 p-2 rounded"
            >
              <span className="text-gray-700 dark:text-gray-100">{ip}</span>
              <button
                onClick={() => handleRemoveIp(idx)}
                className="text-sm text-red-500 hover:text-red-700 transition-colors"
              >
                Remove
              </button>
            </div>
          ))}
          {ipList.length === 0 && (
            <p className="text-gray-500 dark:text-gray-300">No IP addresses added.</p>
          )}
        </div>
        <div className="flex flex-col sm:flex-row gap-3 mt-3">
          <input
            type="text"
            placeholder="e.g. 192.168.1.100"
            className="p-2 border border-gray-300 dark:border-gray-600 rounded 
                       dark:bg-gray-700 dark:text-gray-100 focus:outline-none 
                       focus:ring-2 focus:ring-blue-500 w-full"
            value={newIp}
            onChange={(e) => setNewIp(e.target.value)}
          />
          <button
            onClick={handleAddIp}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white 
                       rounded transition-colors font-medium"
          >
            Add IP
          </button>
        </div>
      </section>

      {/* Save Button */}
      <div className="flex justify-end">
        <button
          onClick={handleSave}
          className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white 
                     font-semibold rounded shadow transition-colors"
        >
          Save Security
        </button>
      </div>
    </div>
  );
};

export default SecuritySettings;
